import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { getClients } from '~/api/clients';
import type { ApiResponse, OptionType } from '../../../../../src/component/PaginatedSelect/type';
import { fetchActivationCodesCsv } from '../logics/fetchCsv';
import type { DownloadActivationCodesFormFieldValues } from '../logics/schema';
import { downloadActivationCodesSchema } from '../logics/schema';
import type { DownloadActivationCodesFormPresentation } from '../presentations';

type PresentationProps = React.ComponentProps<typeof DownloadActivationCodesFormPresentation>;

type IUseDownloadActivationCodesForm = () => {
  state: {
    methods: UseFormReturn<DownloadActivationCodesFormFieldValues>;
    isButtonDisabled: boolean;
    searchAction: PresentationProps['searchAction'];
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
    handleClientChange: PresentationProps['onClientChange'];
  };
};

/**
 * アクティベーションコードCSVダウンロードフォームのカスタムフック
 *
 * @returns state.methods - react-hook-formのメソッド
 * @returns state.isButtonDisabled - ボタン非活性フラグ
 * @returns state.searchAction - 企業選択のページネーション対応検索関数
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 * @returns actions.handleClientChange - 企業選択変更ハンドラ
 */
export const useDownloadActivationCodesForm: IUseDownloadActivationCodesForm = () => {
  const threeLetterCode = getThreeLetterCode();
  if (!threeLetterCode) {
    throw new Error('Three letter code is not available');
  }
  const { showToast } = useToast();
  const [selectedClientName, setSelectedClientName] = useState<string | undefined>(undefined);

  const methods = useForm<DownloadActivationCodesFormFieldValues>({
    defaultValues: {
      clientId: '',
    },
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(downloadActivationCodesSchema),
  });

  const handleOnSubmit = methods.handleSubmit(async formValues => {
    const clientId = Number(formValues.clientId);
    const fileName = selectedClientName ? `${selectedClientName}.csv` : 'activation_codes.csv';
    try {
      const blob = await fetchActivationCodesCsv(threeLetterCode, clientId);
      downloadFile(blob, fileName);
      showToast({
        message: 'CSVをダウンロードしました。',
        status: 'success',
      });
    } catch {
      showToast({
        message: 'CSVのダウンロードに失敗しました。',
        status: 'danger',
      });
    }
  });

  /**
   * 企業選択のページネーション対応検索関数
   */
  const searchAction = async (input: string, page: number): Promise<ApiResponse> => {
    try {
      const { data, headers } = await getClients(undefined, {
        name: input,
        page: page.toString(),
        enabled: '1',
      });
      const options: OptionType[] = data.clients.map(client => ({
        label: client.name,
        value: String(client.id),
      }));
      return {
        options,
        headers: { 'total-pages': headers['total-pages'] },
      };
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
      return { options: [], headers: { 'total-pages': '0' } };
    }
  };

  /**
   * 企業選択の変更ハンドラ
   */
  const handleClientChange: PresentationProps['onClientChange'] = option => {
    methods.setValue('clientId', option ? String(option.value) : '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setSelectedClientName(option?.label);
  };

  return {
    state: {
      methods,
      isButtonDisabled: methods.formState.isSubmitting,
      searchAction,
    },
    actions: { handleOnSubmit, handleClientChange },
  };
};
